import {useRef} from 'react'

import {resolveRawTitleForGroupingContext} from '../../helpers/table-group-utilities'
import type {FieldAggregate} from '../../hooks/use-aggregation-settings'
import {useDroppableGroup} from '../../hooks/use-droppable-group'
import type {ColumnModel} from '../../models/column-model'
import type {GroupedHorizontalGroup, HorizontalGrouping} from '../../models/horizontal-group'
import type {MemexItemModel} from '../../models/memex-item-model'
import type {VerticalGroup} from '../../models/vertical-group'
import {usePaginatedMemexItemsQuery} from '../../state-providers/memex-items/queries/use-paginated-memex-items-query'
import {GroupHeader} from '../common/group/group-header'
import {useScrollGroupIntoViewOnToggle} from '../common/group/hooks/use-scroll-group-into-view-on-toggle'
import {GroupHeaderLabel} from '../common/group/label/group-header-label'
import {OMNIBAR_HEIGHT} from '../omnibar/omnibar'
import styles from './Board.module.css'
import {Columns} from './columns'
import {CARD_MARGIN_BOTTOM, CARD_SIZE_ESTIMATE, COLUMN_PADDING} from './constants'
import useIsVisible from './hooks/use-is-visible'

export function HorizontalGroup({
  aggregates,
  groupByField,
  groupByFieldOptions,
  groupedItems,
  headerRef,
  hideColumn,
  hideItemsCount,
  horizontalGroup,
  index,
  lastNewColumnNameRef,
  scrollToItemId,
  draggingItems,
  scrollRef,
}: {
  horizontalGroup: Readonly<GroupedHorizontalGroup>
  headerRef: React.MutableRefObject<HTMLDivElement | null>
  aggregates: Array<FieldAggregate>
  hideItemsCount: boolean
  groupByFieldOptions: Array<VerticalGroup>
  groupByField: ColumnModel | undefined
  scrollToItemId: number | undefined
  lastNewColumnNameRef: React.MutableRefObject<string | null>
  hideColumn: (verticalGroup: VerticalGroup) => boolean
  groupedItems: HorizontalGrouping
  index: number
  draggingItems: Array<MemexItemModel>
  scrollRef: React.MutableRefObject<HTMLDivElement | null>
}) {
  const ref = useRef<HTMLDivElement>(null)
  const {hasNextPageForSecondaryGroups} = usePaginatedMemexItemsQuery()

  // Find the column with the most cards in it to determine size estimate.
  const maxCards = (Object.values(horizontalGroup.itemsByVerticalGroup) || []).reduce((acc, curr) => {
    return acc > curr.items.length ? acc : curr.items.length
  }, 0)
  const defaultHeight = horizontalGroup.isCollapsed
    ? 0
    : maxCards * (CARD_SIZE_ESTIMATE + CARD_MARGIN_BOTTOM) + OMNIBAR_HEIGHT + COLUMN_PADDING * 2
  const {isVisible, size} = useIsVisible({
    ref,
    defaultHeight,
  })

  // If we're dragging a card in a current group, ensure that it is rendered
  const isDraggingInGroup = draggingItems.some(item => horizontalGroup.rows.includes(item))

  const {setNodeRef, isOver} = useDroppableGroup({
    groupId: horizontalGroup.value,
    groupedValue: horizontalGroup.value,
    isCollapsed: horizontalGroup.isCollapsed,
    isEmpty: horizontalGroup.rows.length === 0,
  })

  // When this group of columns is visible, we set the height to 'unset' so that we inherit the true
  // height of the chunk after all of its contents have been painted. Otherwise, we render a fixed
  // height as a placeholder.
  const height = isVisible ? 'unset' : `${size}px`

  const shouldRender = (isVisible || isDraggingInGroup) && !horizontalGroup.isCollapsed

  useScrollGroupIntoViewOnToggle({
    isCollapsed: horizontalGroup.isCollapsed,
    groupRef: ref,
    scrollRef,
    headerRef,
  })

  const isLastGroup = !hasNextPageForSecondaryGroups && groupedItems.horizontalGroups.length - 1 === index

  return (
    <>
      <GroupHeader
        sticky
        isCollapsed={horizontalGroup.isCollapsed}
        metadata={horizontalGroup}
        itemsInGroup={horizontalGroup.rows}
        viewHeaderRef={headerRef}
        ref={setNodeRef}
        isHighlighted={isOver}
      >
        <GroupHeaderLabel
          sourceObject={horizontalGroup.sourceObject}
          rowCount={horizontalGroup.totalCount}
          aggregates={aggregates}
          hideItemsCount={hideItemsCount}
        />
      </GroupHeader>
      <div ref={ref} style={{height}}>
        {shouldRender ? (
          <div
            className={styles.horizontalGroupContainer}
            data-board-horizontal-group={resolveRawTitleForGroupingContext(horizontalGroup.sourceObject)}
          >
            <Columns
              groupByFieldOptions={groupByFieldOptions}
              itemsGroupedByField={horizontalGroup.itemsByVerticalGroup}
              groupByField={groupByField}
              scrollToItemId={scrollToItemId}
              lastNewColumnNameRef={lastNewColumnNameRef}
              headerType="hidden"
              hideColumn={hideColumn}
              horizontalGroup={horizontalGroup}
              isLastGroup={isLastGroup}
              horizontalGroupIndex={index}
            />
          </div>
        ) : null}
      </div>
    </>
  )
}
